import { html, PropertyValueMap } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { getTax } from "./estimated-tax.selectors";
import { getEstimatedTax } from "./estimated-tax.thunks";
import store from "../../store/store";
import { nothing } from 'lit-html';
import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import { isTunnelPurchaseFlow } from '../../common/utils/util';
import { BaseComponent } from "../../features/base-component/base-component";
import { ActionStatus } from "../../common/constants/action-status";
import emit from "../../common/events";
import { smallSpinner } from '../../common/constants/api-constants';
import { StoreState } from "../../store/store.interface";
import { getCartAnalyticsOverrides } from '../cart/shop-cart/shop-cart.analytics';

@customElement('mhecomm-estimated-tax')
export class EstimatedTax extends connect(store)(BaseComponent) {

    @property()
    storeCode?: string;

    @state()
    private isApplyDisabled = true;

    @state()
    private isInputDisabled = true;

    @state()
    private status = ActionStatus.idle;

    @state()
    private isTaxEstimated = false;

    @state()
    private estimatedTax: number | undefined;

    @state()
    private zipCode = '';

    @state()
    private isZipcodeError = false;

    @state()
    private estimatedTotal?: number;

    private collapse: any;

    renderComp() {
        return html `
        <div part="cartEstimatedTax">
            <div class="d-flex justify-content-between">
                <span>
                    ${isTunnelPurchaseFlow() ? "Tax" : "Estimated tax"}${!this.isTaxEstimated ? ":" : nothing}
                    ${this.isTaxEstimated && !this.isZipcodeError ? html`
                        for <span class="fw-semibold" id="estimatedTaxValue">${this.zipCode}:</span>
                            <a
                            href="javascript:;"
                            id="editZipCode"
                            class="fs-small ms-1 text-decoration-none "
                            aria-label="Edit zip code"
                            @click=${this._handleEdit}>Edit</a>
                    ` : nothing}
                </span>
                ${this.isTaxEstimated ? html`
                    <span class="fs-large fw-bold">
                        ${this.status === ActionStatus.loading ? smallSpinner() : html`$${this.estimatedTax?.toFixed(2)}`}
                    </span>
                ` : isTunnelPurchaseFlow() ? html`<span class="fw-light fs-small">Calculated on next step</span>` : html`
                    <a
                        role="button"
                        class="text-decoration-none fs-small"
                        data-bs-target="#taxCollapseSection"
                        data-bs-toggle="collapse"
                        aria-controls="taxCollapseSection"
                        href="javascript:;"
                        aria-expanded="false"
                        aria-controls="tax-section"
                        id="zipCodeLink"
                        @click=${this._handleCollapseToggle}
                        @keyup=${this._handleCollapseToggle}>
                        Enter ZIP code${this.uxtIconReady ? html`<uxt-icon id="enterDropDownIcon" class="ms-1" icon="chevron-down-sm"></uxt-icon>` : nothing}</a>
                `}
            </div>
            ${!this.isTaxEstimated ? html`
                <div class="mt-2 row collapse" id="taxCollapseSection">
                    <div class="col-12 col-md-8">
                        <input type="text" class="form-control ${this.isZipcodeError ? 'is-invalid': ''}" ?disabled=${this.isInputDisabled} id="taxZipCode" aria-label="zip code" @keyup=${this._handleZipCodeUpdate} placeholder="Enter zip code"/>
                        <p aria-live="assertive" aria-atomic="true" class="my-2 invalid-feedback d-block">
                            ${this.isZipcodeError ? html`
                                <span>${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle"></uxt-icon>` : ''}<span class="visually-hidden">Error&nbsp;</span>Please enter a valid zip code.</span>
                            `: nothing}
                        </p>
                    </div>
                    <div class="col-12 col-md-4">
                        <button class="btn btn-primary w-100 mt-2 mt-md-0" ?disabled=${this.isApplyDisabled} id="taxApplyBtn" @click=${this._calculateTax}>
                            ${ this.status === ActionStatus.loading ? html`
                                <div class="spinner-border spinner-border-sm" role="status"></div>
                            ` : 'Apply' }
                        </button>
                    </div>
                </div>
            ` : nothing}
            <p aria-live="assertive" aria-atomic="true" class="visually-hidden">
                ${ this.isTaxEstimated ? html`
                    <span>Estimated tax for ${this.zipCode} is $${this.estimatedTax?.toFixed(2)}. Estimated total updated to $${this.estimatedTotal?.toFixed(2)}.</span>
                `: nothing}
                ${ !this.isTaxEstimated && this.status === ActionStatus.loading ? html`
                    <span>Loading estimated tax...</span>
                `: nothing}
            </p>
        </div>`
    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
    }

    protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
        super.updated(_changedProperties);
        this.collapse = this.bsInit('#taxCollapseSection', window.bootstrap?.Collapse, { toggle: false })?.instance;
    }

    _handleCollapseToggle(event, isEdit?: boolean) {
        if(isEdit || (event && (event.type == 'click' || (event.type == 'keyup' && event.code == 'Space')))) {
            this.collapse.toggle();
            const zipLink = this.shadowRoot!.querySelector('#zipCodeLink') as HTMLLinkElement;
            const dropDownIcon = this.shadowRoot!.querySelector('#enterDropDownIcon');
            if (zipLink.getAttribute('aria-expanded') == 'true'){
                zipLink.setAttribute('aria-expanded', 'false');
                dropDownIcon?.setAttribute('icon', 'chevron-down-sm');
            } else {
                zipLink.setAttribute('aria-expanded', 'true');
                dropDownIcon?.setAttribute('icon', 'chevron-up-sm');
            }
            isEdit ?  zipLink.setAttribute('aria-expanded', 'true') : '';
        }
        const eventName = isEdit ? 'click-edit-zip-code-link' : 'click-zip-code-link';
        emit({
            element: this,
            name: eventName,
            dataLayer: true
        }); 
    }

    _handleZipCodeUpdate() {
        this.isZipcodeError = false;
        const inputElem = this.getZipCodeInputElement();
        this.zipCode = inputElem.value;
        if(this.zipCode?.length < 1) {
            this.isApplyDisabled = true;
        } else {
            this.isApplyDisabled = false;
        }
    }

    _handleEdit(event) {
        this.isTaxEstimated = false;
        this._handleCollapseToggle(event, true);
        const zipInputElem = this.getZipCodeInputElement();
        this.getZipCodeInputElement().value = this.zipCode;
        setTimeout(()=> {zipInputElem.focus();}, 500);
    }

    async _calculateTax() {
        const inputElem = this.getZipCodeInputElement();
        this.zipCode = inputElem.value;
        await store.dispatch(getEstimatedTax({ zipCode: this.zipCode, storeCode: this.storeCode} ));
        const state = store.getState() as StoreState;
        emit({
            element: this,
            name: 'estimated-tax',
            detail: {
                id: this.id,
                user: getUserDataForUserAnalytics(state),
                estimatedTax: getTax(state)?.totalTax.amount,
                zipCode: this.zipCode,
                url: window.location.href,
                ...getCartAnalyticsOverrides(state)
            },
        });
    }

    stateChanged(state: StoreState) {
        this.status = state.estimatedTaxState?.status;

        if (state.estimatedTaxState?.status === ActionStatus.idle) {
            this.isTaxEstimated = false;
            this.isInputDisabled = false;
            this.isApplyDisabled = true;
        } else if (state.estimatedTaxState?.status === ActionStatus.loading) {
            this.isInputDisabled = true;
            this.isApplyDisabled = true;
        } else if (state.estimatedTaxState?.status === ActionStatus.succeeded) {
            this.isInputDisabled = false;
            this.isApplyDisabled = true;
            this.isZipcodeError = false;
            this.estimatedTax = getTax(state)?.totalTax?.amount;
            this.isTaxEstimated = true;
            const cartTotal = state?.shopCartState?.getShopCart?.data?.totalAmount;
            if (this.estimatedTax && cartTotal) {
                this.estimatedTotal = cartTotal + this.estimatedTax;
            }

            if (!this.zipCode && state.estimatedTaxState.data?.zipCode) {
                // Only update the zip code if the user has not manually used the input field.
                this.zipCode = state.estimatedTaxState.data?.zipCode;
            }
        } else if (state.estimatedTaxState?.status === ActionStatus.failed) {
            this.isInputDisabled = false;
            this.isApplyDisabled = true;
            if (state.estimatedTaxState?.error?.responseStatus == 400) {
                this.isTaxEstimated = false;
                this.isZipcodeError = true;
                const zipInputElem = this.getZipCodeInputElement()
                zipInputElem.focus();
            }
        }
    }



    getZipCodeInputElement(){
        return this.shadowRoot!.querySelector('#taxZipCode') as HTMLInputElement;
    }
}


declare global {
    interface HTMLElementTagNameMap {
      'mhecomm-estimated-tax': EstimatedTax;
    }
  }
